import { type FC, useMemo, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { Chip } from '@/components/commons/chip'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { AlertTriangleIcon, CheckIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import {
	OpsVettingDisabledContainer,
	OpsVettingGalleryGrid,
	OpsVettingParagraph,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useGalleryNeedsReviewShortcuts } from '@/features/photoset/hooks/use-gallery-needs-review-shortcuts'
import { ServiceVettingCriteriaGalleryItem } from '@/features/service-vetting/components/service-vetting-criteria-gallery-item'
import { ServiceVettingCriteriaGalleryUploadButton } from '@/features/service-vetting/components/service-vetting-criteria-gallery-upload-button'
import { useAddServiceGalleryFlags } from '@/features/service-vetting/query/use-add-service-gallery-flags.ts'
import { useGetServiceFlags } from '@/features/service-vetting/query/use-get-service-flags.ts'
import { useRemoveServiceGalleryFlags } from '@/features/service-vetting/query/use-remove-service-gallery-flags.ts'
import { useUpdateService } from '@/features/service-vetting/query/use-update-service.ts'
import { useServicePhotoUpload } from '@/features/service-vetting/store/services-photo-upload.ts'
import { useServiceVettingError } from '@/features/service-vetting/store/use-service-vetting-error.ts'
import { useTranslation } from '@/translations/i18n'
import type { ServiceGalleryImage } from '../types'
import { ServiceVettingCriteriaCoverPhoto } from './service-vetting-criteria-cover-photo'
import { ServiceVettingCriteriaUploadedItem } from './service-vetting-criteria-uploaded'
import { ServiceVettingCriteriaUploadingItem } from './service-vetting-criteria-uploading-item'

interface ServiceVettingCriteriaGalleryProps {
	hideAction: boolean
	isExpertiseDeclined: boolean
	galleryId: number
	galleryImages: ServiceGalleryImage[]
	serviceId: number
	isProPhotographyRequired: boolean
	lackPhotoVariety: boolean
}

export const ServiceVettingCriteriaGallery: FC<
	ServiceVettingCriteriaGalleryProps
> = ({
	hideAction,
	isExpertiseDeclined,
	galleryId,
	galleryImages,
	serviceId,
	isProPhotographyRequired,
	lackPhotoVariety
}) => {
	const { t } = useTranslation()

	const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
		null
	)
	const [showGalleryModal, setShowGalleryModal] = useState(false)

	const filteredGalleryImages = useMemo(
		() =>
			galleryImages.filter(
				(img) => !img.is_offering_image && img.is_approved !== false
			) || [],
		[galleryImages]
	)

	const coverPhoto = useMemo(
		() => galleryImages.find((img) => img.is_cover),
		[galleryImages]
	)

	const uploadingItems = useServicePhotoUpload((store) => store.galleryItems)

	const sortedGalleryImages = useMemo(() => {
		const filteredImages =
			galleryImages.filter(
				(image) =>
					image.is_cover ||
					(!image.is_offering_image && image.is_approved !== false)
			) || []

		// Sort the images according to the specified priority
		return filteredImages.sort((a, b) => {
			// Cover images come first
			if (a.is_cover && !b.is_cover) {
				return -1
			}
			if (!a.is_cover && b.is_cover) {
				return 1
			}

			// If all criteria are the same, maintain original order
			return 0
		})
	}, [galleryImages])

	const errors = useServiceVettingError((state) => state.errors)

	const flagsQuery = useGetServiceFlags({
		response_type: 'extended',
		types: ['gallery']
	})
	const addServiceGalleryFlags = useAddServiceGalleryFlags({
		serviceId,
		itemId: galleryId
	})
	const removeServiceGalleryFlags = useRemoveServiceGalleryFlags({
		serviceId,
		itemId: galleryId
	})
	const updateService = useUpdateService({ serviceId })

	const onChangeCheckBox = (isChecked: boolean) => {
		const flag = flagsQuery.data?.find(
			(flag) => flag.key_name === 'lack_photo_variety'
		)
		const mutationFunction = isChecked
			? addServiceGalleryFlags
			: removeServiceGalleryFlags
		if (flag) {
			mutationFunction.mutate({ flags: [flag] })
		}
	}
	const handleClickExpandedImage = (imageId: ServiceGalleryImage['id']) => {
		const index = sortedGalleryImages.findIndex(
			(image) => image.id === imageId
		)
		setExpandedImageIndex(index)
		setShowGalleryModal(true)
	}

	const handleCloseModal = () => {
		setShowGalleryModal(false)
		setExpandedImageIndex(null)
	}

	useGalleryNeedsReviewShortcuts({
		selectableImages: sortedGalleryImages,
		currentDetailImageIndex: expandedImageIndex || 0,
		setCurrentDetailImageIndex: setExpandedImageIndex,
		onClose: handleCloseModal
	})

	return (
		<OpsVettingSectionWrapper direction={'column'} fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<div className='flex items-center gap-8'>
					<OpsVettingSectionTitle>
						{t('vetting:criteria.service.gallery.title')}
					</OpsVettingSectionTitle>
					{!hideAction && (
						<ServiceVettingCriteriaGalleryUploadButton
							disabled={isExpertiseDeclined}
							galleryId={galleryId}
							serviceId={serviceId}
						/>
					)}
					<Checkbox
						checked={lackPhotoVariety}
						disabled={hideAction || isExpertiseDeclined}
						id={'lack_photo_variety'}
						label={t(
							'vetting:criteria.service.flags_key_name.lack_photo_variety'
						)}
						onChange={(e) =>
							onChangeCheckBox(e.currentTarget.checked)
						}
					/>
					<Checkbox
						checked={isProPhotographyRequired}
						disabled={isExpertiseDeclined}
						id={'requires_pro_photography'}
						label={t(
							'vetting:criteria.commons.requires_pro_photography'
						)}
						onChange={(e) =>
							updateService.mutate({
								is_pro_photography_required:
									e.currentTarget.checked
							})
						}
					/>
				</div>
				<div className='flex items-center gap-4'>
					{errors.galleryErrors.map((error) => (
						<Chip
							isActive
							key={error}
							label={t(error)}
							startSlot={<AlertTriangleIcon />}
							variant={'danger'}
						/>
					))}
					<OpsVettingParagraph>
						{filteredGalleryImages.filter(
							(image) => image.is_approved === null
						).length > 0 ? (
							t(
								'vetting:criteria.commons.gallery.images_remaining',
								{
									value: filteredGalleryImages.filter(
										(image) => image.is_approved === null
									).length
								}
							)
						) : (
							<span className='flex gap-1'>
								<CheckIcon
									className='fill-success-600'
									size={20}
								/>
								{t(
									'vetting:criteria.commons.gallery.images_reviewed'
								)}
							</span>
						)}
					</OpsVettingParagraph>
				</div>
			</div>
			<Separator orientation='horizontal' />
			<OpsVettingDisabledContainer $isDisabled={isExpertiseDeclined}>
				<OpsVettingGalleryGrid>
					<ServiceVettingCriteriaCoverPhoto
						coverPhoto={coverPhoto}
						galleryId={galleryId}
						hideAction={hideAction}
						onClickExpand={() =>
							handleClickExpandedImage(coverPhoto?.id || 0)
						}
						serviceId={serviceId}
					/>
					{filteredGalleryImages.map((image) =>
						image.is_host_approval_needed ? (
							<ServiceVettingCriteriaUploadedItem
								galleryId={galleryId}
								hideAction={hideAction}
								image={image}
								key={image.id}
								onClickExpand={() =>
									handleClickExpandedImage(image.id)
								}
								serviceId={serviceId}
							/>
						) : (
							<ServiceVettingCriteriaGalleryItem
								galleryId={galleryId}
								hideAction={hideAction}
								image={image}
								key={image.id}
								onClickExpand={() =>
									handleClickExpandedImage(image.id)
								}
								serviceId={serviceId}
							/>
						)
					)}
					{uploadingItems.map((image) => (
						<ServiceVettingCriteriaUploadingItem
							image={image}
							key={image.id}
						/>
					))}
				</OpsVettingGalleryGrid>
			</OpsVettingDisabledContainer>
			{showGalleryModal && expandedImageIndex !== null && (
				<GalleryModal
					currentImage={sortedGalleryImages[expandedImageIndex]}
					headerSlot={
						<GalleryModalHeader
							currentImage={
								sortedGalleryImages[expandedImageIndex]
							}
							imagesCount={sortedGalleryImages.length}
							onClose={handleCloseModal}
						/>
					}
					onClose={handleCloseModal}
					thumbnails={{
						l: sortedGalleryImages[expandedImageIndex].url || '',
						m: '',
						s: '',
						xs: ''
					}}
					thumbnailsSlot={
						<GalleryModalThumbnails
							currentImageId={
								sortedGalleryImages[expandedImageIndex].id
							}
							images={sortedGalleryImages.map((image) => ({
								id: image.id,
								name: image.name,
								thumbnails: {
									l: image.url || '',
									m: image.url || '',
									s: image.url || '',
									xs: image.url || ''
								}
							}))}
							onSelect={(_, index) => {
								setExpandedImageIndex(index)
							}}
							thumbnails='thumbnails'
						/>
					}
				/>
			)}
		</OpsVettingSectionWrapper>
	)
}
